import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { get_form_url } from '@/utils/form';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setSubmitStatus('success');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    try {
      const formUrl = get_form_url('Newsletter Signup');
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          payload.append(key, value as string);
        }
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || 'Wystąpił błąd. Spróbuj ponownie.');
        setSubmitStatus('error');
      }
    } catch {
      setFormError('Wystąpił błąd sieci. Spróbuj ponownie.');
      setSubmitStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-900 rounded-t-3xl mt-16">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          <div>
            <h3 className="text-2xl md:text-3xl font-heading text-background-50 mb-4">
              {t('footer.subscribeTitle')}
            </h3>
            <p className="text-foreground-300 text-sm leading-relaxed mb-6">
              {t('footer.subscribeDesc')}
            </p>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form="" className="flex flex-col gap-3">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="antispam-control"
              />
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                className="bg-transparent border-b border-foreground-600 text-background-50 text-sm py-2 px-1 focus:outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-500"
              />
              <button
                type="submit"
                className="bg-background-50 text-foreground-900 text-sm font-medium px-5 py-2.5 rounded-full hover:bg-background-100 transition-all whitespace-nowrap self-start cursor-pointer"
              >
                {t('footer.subscribeBtn')}
              </button>
              {submitStatus === 'success' && (
                <p className="text-accent-400 text-xs">Dziękujemy za zapisanie się!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs">{formError}</p>
              )}
            </form>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              {t('footer.navTitle')}
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-foreground-300 text-sm hover:text-primary-400 transition-colors underline cursor-pointer">
                  {t('footer.navHome')}
                </Link>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-300 text-sm hover:text-primary-400 transition-colors underline cursor-pointer"
                >
                  {t('footer.navSpreadsheet')}
                </a>
              </li>
              <li>
                <Link to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik" className="text-foreground-300 text-sm hover:text-primary-400 transition-colors underline cursor-pointer">
                  {t('footer.navGuide')}
                </Link>
              </li>
              <li>
                <Link to="/artykul/kakobuy-vs-pandabuy-porownanie" className="text-foreground-300 text-sm hover:text-primary-400 transition-colors underline cursor-pointer">
                  Kakobuy vs Pandabuy
                </Link>
              </li>
              <li>
                <Link to="/artykul/jak-czytac-zdjecia-qc-kakobuy" className="text-foreground-300 text-sm hover:text-primary-400 transition-colors underline cursor-pointer">
                  Jak czytać zdjęcia QC
                </Link>
              </li>
              <li>
                <a href="#faq" className="text-foreground-300 text-sm hover:text-primary-400 transition-colors underline cursor-pointer">
                  {t('footer.navFAQ')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              {t('footer.contactTitle')}
            </h4>
            <div className="space-y-3 text-foreground-300 text-sm">
              <p>kontakt@kakobuyfinds.fit</p>
              <p className="mt-6 text-foreground-500 text-xs">Polska</p>
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-700 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <a href="#" className="text-foreground-400 text-xs hover:text-primary-400 transition-colors underline cursor-pointer">
              Pinterest
            </a>
            <a href="#" className="text-foreground-400 text-xs hover:text-primary-400 transition-colors underline cursor-pointer">
              Reddit
            </a>
            <a href="#" className="text-foreground-400 text-xs hover:text-primary-400 transition-colors underline cursor-pointer">
              Telegram
            </a>
          </div>
          <p className="text-foreground-500 text-xs text-center">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-foreground-400 text-xs hover:text-primary-400 transition-colors underline cursor-pointer">
              {t('footer.privacy')}
            </a>
            <a href="#" className="text-foreground-400 text-xs hover:text-primary-400 transition-colors underline cursor-pointer">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}